/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';

import HealthEntryCategoryNames from '../../../../constants/HealthEntryCategoryNames';

import { ShareCardSummaryProfile } from './ShareCardSummaryProfile';
import ShareCardSummaryGroupContents from './ShareCardSummaryGroupContents';
import ShareCardSummaryPublicUrl from './ShareCardSummaryPublicUrl';
import { ShareCardSummaryHealthEntryGroup, ShareCardSummaryAllergyGroup, ShareCardSummaryTrackableGroup } from './ShareCardSummaryOccurrenceGroup';
import ShareCardSummaryGroup from './ShareCardSummaryGroup';
import ShareCardSummaryFiles from './ShareCardSummaryFiles';
import ShareCardSummaryNotes from './ShareCardSummaryNotes';
import ShareCardSummaryContacts from './ShareCardSummaryContacts';
import ShareCardSummaryMedicalJewelry from './ShareCardSummaryMedicalJewelry';
import ViewShareCardWallpaperSetup from './ViewShareCardWallpaperSetup';

import ShareCardIncludedTypeNames from '../../../../constants/ShareCardIncludedTypeNames';

import NoteModel from '../../../../models/NoteModel';
import ShareCardModel, { HealthEntries } from '../../../../models/ShareCardModel';
import ProfileModel from '../../../../models/ProfileModel';
import { ShareCardColorModel } from '../../../appAuthorized/dataTransformComponents/ColorShareCard';

import { Caption } from '../../../components/Text';
import ContactModel, { ContactModelType } from '../../../../models/ContactModel';
import ConnectedNestedIntlProvider from '../../../localization/ConnectedNestedIntlProvider';

interface Props {
  shareCard: ShareCardModel;
  healthEntries: HealthEntries;
  profile: ProfileModel;
  languageCode: string;
  shareCardColorModel: ShareCardColorModel;
  onEnableUrl(): void;
  files: any[];
  notes: NoteModel[];
  contacts: ContactModelType[];
  appLevelLocale: string;
}

const ShareCardSummaryContent = (props: Props) => {
  const shareCardData = props.shareCard.data;

  return (
    <div>
      <ShareCardSummaryPublicUrl
        profile={props.profile}
        shareCard={props.shareCard}
        languageCode={props.languageCode}
        shareCardColorModel={props.shareCardColorModel}
        onEnableUrl={props.onEnableUrl}
      />

      <ShareCardSummaryMedicalJewelry
        shareCardId={props.shareCard.id}
        shareCardColorModel={props.shareCardColorModel}
        shareCard={props.shareCard}
        languageCode={props.languageCode}
      />

      <ConnectedNestedIntlProvider locale={props.appLevelLocale}>
        <ViewShareCardWallpaperSetup
          WrapperComponent={ShareCardSummaryGroup}
          shareCardColorModel={props.shareCardColorModel}
          shareCardData={shareCardData}
          isEditMode={false}
          onEnableUrl={props.onEnableUrl}
        />
      </ConnectedNestedIntlProvider>

      <ShareCardSummaryProfile
        profile={props.profile}
        shareCard={props.shareCard}
        languageCode={props.languageCode}
      />

      <ShareCardSummaryGroupContents
        shareCard={props.shareCard}
        languageCode={props.languageCode}
        shareCardColorModel={props.shareCardColorModel}
      />

      <ShareCardSummaryAllergyGroup
        categoryName={HealthEntryCategoryNames.allergy}
        newerCollection={props.healthEntries[HealthEntryCategoryNames.allergy].newer}
        olderCollection={props.healthEntries[HealthEntryCategoryNames.allergy].older}
        shareCard={props.shareCard}
        languageCode={props.languageCode}
        includedValues={ShareCardIncludedTypeNames.latest}
        hasTitle
      />
      <ShareCardSummaryHealthEntryGroup
        categoryName={HealthEntryCategoryNames.condition}
        newerCollection={props.healthEntries[HealthEntryCategoryNames.condition].newer}
        olderCollection={props.healthEntries[HealthEntryCategoryNames.condition].older}
        shareCard={props.shareCard}
        languageCode={props.languageCode}
        includedValues={ShareCardIncludedTypeNames.latest}
        hasTitle
      />
      <ShareCardSummaryHealthEntryGroup
        categoryName={HealthEntryCategoryNames.medication}
        newerCollection={props.healthEntries[HealthEntryCategoryNames.medication].newer}
        olderCollection={props.healthEntries[HealthEntryCategoryNames.medication].older}
        shareCard={props.shareCard}
        languageCode={props.languageCode}
        includedValues={ShareCardIncludedTypeNames.latest}
        hasTitle
      />
      <ShareCardSummaryHealthEntryGroup
        categoryName={HealthEntryCategoryNames.otherTreatment}
        newerCollection={props.healthEntries[HealthEntryCategoryNames.otherTreatment].newer}
        olderCollection={props.healthEntries[HealthEntryCategoryNames.otherTreatment].older}
        shareCard={props.shareCard}
        languageCode={props.languageCode}
        includedValues={ShareCardIncludedTypeNames.latest}
        hasTitle
      />
      <ShareCardSummaryHealthEntryGroup
        categoryName={HealthEntryCategoryNames.procedure}
        newerCollection={props.healthEntries[HealthEntryCategoryNames.procedure].all}
        olderCollection={[]}
        shareCard={props.shareCard}
        languageCode={props.languageCode}
        includedValues={ShareCardIncludedTypeNames.latest}
      />
      <ShareCardSummaryHealthEntryGroup
        categoryName={HealthEntryCategoryNames.vaccination}
        newerCollection={props.healthEntries[HealthEntryCategoryNames.vaccination].all}
        olderCollection={[]}
        shareCard={props.shareCard}
        languageCode={props.languageCode}
        includedValues={ShareCardIncludedTypeNames.latest}
      />

      <ShareCardSummaryHealthEntryGroup
        categoryName={HealthEntryCategoryNames.measurement}
        newerCollection={props.healthEntries[HealthEntryCategoryNames.measurement].all}
        olderCollection={[]}
        shareCard={props.shareCard}
        languageCode={props.languageCode}
        includedValues={ShareCardIncludedTypeNames.latest}
      />

      <ShareCardSummaryHealthEntryGroup
        categoryName={HealthEntryCategoryNames.lab}
        newerCollection={props.healthEntries[HealthEntryCategoryNames.lab].all}
        olderCollection={[]}
        shareCard={props.shareCard}
        languageCode={props.languageCode}
        includedValues={ShareCardIncludedTypeNames.latest}
      />

      <ShareCardSummaryHealthEntryGroup
        categoryName={HealthEntryCategoryNames.geneticResult}
        newerCollection={props.healthEntries[HealthEntryCategoryNames.geneticResult].all}
        olderCollection={[]}
        shareCard={props.shareCard}
        languageCode={props.languageCode}
        includedValues={ShareCardIncludedTypeNames.latest}
      />

      <ShareCardSummaryTrackableGroup
        includedValues={ShareCardIncludedTypeNames.latest}
        languageCode={props.languageCode}
        shareCard={shareCardData}
      />

      <ShareCardSummaryHealthEntryGroup
        categoryName={HealthEntryCategoryNames.measurement}
        newerCollection={props.healthEntries[HealthEntryCategoryNames.measurement].all}
        olderCollection={[]}
        shareCard={props.shareCard}
        languageCode={props.languageCode}
        includedValues={ShareCardIncludedTypeNames.all}
      />

      <ShareCardSummaryHealthEntryGroup
        categoryName={HealthEntryCategoryNames.lab}
        newerCollection={props.healthEntries[HealthEntryCategoryNames.lab].all}
        olderCollection={[]}
        shareCard={props.shareCard}
        languageCode={props.languageCode}
        includedValues={ShareCardIncludedTypeNames.all}
      />

      <ShareCardSummaryHealthEntryGroup
        categoryName={HealthEntryCategoryNames.geneticResult}
        newerCollection={props.healthEntries[HealthEntryCategoryNames.geneticResult].all}
        olderCollection={[]}
        shareCard={props.shareCard}
        languageCode={props.languageCode}
        includedValues={ShareCardIncludedTypeNames.all}
      />

      <ShareCardSummaryTrackableGroup
        includedValues={ShareCardIncludedTypeNames.all}
        languageCode={props.languageCode}
        shareCard={shareCardData}
      />

      <ShareCardSummaryContacts
        categoryName="contacts"
        contacts={props.contacts.filter(c => !ContactModel.isProvider(c))}
        shareCard={props.shareCard}
        languageCode={props.languageCode}
      />

      <ShareCardSummaryContacts
        categoryName="providerContacts"
        contacts={props.contacts.filter(c => ContactModel.isProvider(c))}
        shareCard={props.shareCard}
        languageCode={props.languageCode}
      />

      <ShareCardSummaryFiles
        newerCollection={props.files}
        shareCard={props.shareCard}
        languageCode={props.languageCode}
      />

      <ShareCardSummaryNotes
        newerCollection={props.notes}
        shareCard={props.shareCard}
        languageCode={props.languageCode}
      />

      {
        props.shareCard.comment.hasComment() ? (
          <ShareCardSummaryGroup>
            <Caption>
              {props.shareCard.comment.get()}
            </Caption>
          </ShareCardSummaryGroup>
        ) : null
      }
    </div>
  );
};

export default ShareCardSummaryContent;
